import React, { useEffect }  from 'react';
import { array, bool, func, object, string } from 'prop-types';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { get, isEmpty } from 'lodash';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import EsmsDialog from '../../components/EsmsDialog';

import * as ArchitectActions from '../../actions/architectActions';
import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as SiteActions from '../../actions/siteActions';
import * as UiActions from '../../actions/uiActions';

import classes from './EditEntityModal.module.css';

const ValidationSchema = Yup.object().shape({
    client: Yup.string()
        .required(),
    site: Yup.string()
        .required(),
    name: Yup.string()
        .max(45)
        .required(),
    systemType: Yup.string()
        .required()
});

const EditSystemModal = ({
    close,
    clearErrors,
    fetchClientSites,
    open,
    title,
    isLoading,
    errors,
    clients,
    systemTypes,
    toggleLoading,
    ...props
}) => {

    useEffect(() => {
        async function loadSites() {
            await fetchClientSites(get(props, 'selectedClient.id', ''));
        }

        if (!isEmpty(get(props, 'selectedClient.id', '')) && open) {
            loadSites();
        }
    }, [props.selectedClient, open]);

    const formik = useFormik({
        initialValues: {
            client:     get(props, 'selectedClient.id', ''),
            site:       get(props, 'selectedSite.id', ''),
            name:       get(props, 'selectedNode.name', ''),
            systemType: get(props, 'selectedNode.systemType.id', '')
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                companyInfo,
                updateSystem,
                selectedNode
            } = props;

            const body = {
                name:         values.name,
                systemTypeId: values.systemType
            };

            let isUpdated;

            clearErrors();

            if (!isLoading) {
                toggleLoading(true);
                isUpdated = await updateSystem(selectedNode.id, companyInfo, body);
            }

            if (isUpdated) handleClose();
        }
    });

    const handleClose = () => {
        clearErrors();
        toggleLoading(false);
        close();
    };

    const handleClientChange = async event => {
        const { value } = event.target;

        formik.setFieldValue('site', '', false);

        formik.setTouched({
            site: false
        });

        toggleLoading(false);
        await fetchClientSites(value);

        formik.handleChange(event);
    };

    if (isEmpty(props.sites)) return null;

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={formik.handleReset}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Update"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/systemTreeIcon.svg" alt="new system icon" />
                        <span>Edit System</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Choose a unique name for this system.
                        </p>
                        <p>
                            Type the system type for this system.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="client"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                            >
                                Client
                            </InputLabel>
                            <Select
                                value={formik.values.client}
                                onChange={handleClientChange}
                                className={classes.select}
                                disabled={true}
                                inputProps={{
                                    name:   'client',
                                    id:     'client',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                            >
                                {
                                    clients.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.client &&
                                formik.errors.client && <FormHelperText className={classes.error}>{formik.errors.client}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="site"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                            >
                                Site
                            </InputLabel>
                            <Select
                                value={formik.values.site}
                                onChange={formik.handleChange}
                                disabled={true}
                                className={classes.select}
                                inputProps={{
                                    name:   'site',
                                    id:     'site',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                            >
                                {
                                    props.sites.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.site &&
                                formik.errors.site && <FormHelperText className={classes.error}>{formik.errors.site}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="systemType"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.systemType) && Boolean(formik.touched.systemType)}
                            >
                                System Type
                            </InputLabel>
                            <Select
                                value={formik.values.systemType}
                                onChange={formik.handleChange}
                                className={classes.select}
                                inputProps={{
                                    name:   'systemType',
                                    id:     'systemType',
                                    onBlur: formik.handleBlur
                                }}
                            >
                                {
                                    systemTypes.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.site &&
                                formik.errors.site && <FormHelperText className={classes.error}>{formik.errors.site}</FormHelperText>}
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

EditSystemModal.defaultProps = {
    clients:        [],
    sites:          [],
    selectedClient: {},
    selectedNode:   {},
    selectedSite:   {},
    systemTypes:    []

};

EditSystemModal.propTypes = {
    clients:        array,
    close:          func.isRequired,
    open:           bool.isRequired,
    sites:          array,
    selectedClient: object,
    selectedNode:   object,
    selectedSite:   object,
    systemTypes:    array,
    title:          string.isRequired
};

const mapStateToProps = state => {
    return {
        clients:        state.architect.clientNodes,
        companyInfo:    state.companyInfo,
        errors:         state.messages.errors,
        isLoading:      state.ui.isLoading,
        selectedClient: state.client.selectedClient,
        selectedNode:   state.architect.selectedNode,
        selectedSite:   state.site.selectedSite,
        sites:          state.site.sites,
        systemTypes:    state.systemTypes.data
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...SiteActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(EditSystemModal);
